import React, { useCallback, useEffect, useState } from 'react'
import { Plus, AlertTriangle, History, Loader2, Ticket, Pencil, Trash2 } from 'lucide-react'
import { useToast } from '../../contexts/ToastContext'
import { AllowanceForm, AdjustmentForm } from './BroadcastEntitlementForms'
import { REASONS } from './broadcastConstants'
import broadcastAdminService, {
  BroadcastAllowance, BroadcastEntitlement as Entitlement, BroadcastAdjustment
} from '../../services/broadcastAdminService'

interface Props {
  customerId: string
  organizationName?: string
}

const fmtDate = (d: string) => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—')

const BroadcastEntitlement: React.FC<Props> = ({ customerId, organizationName }) => {
  const { showSuccess, showError } = useToast()
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null)
  const [allowances, setAllowances] = useState<BroadcastAllowance[]>([])
  const [adjustments, setAdjustments] = useState<BroadcastAdjustment[]>([])
  const [loading, setLoading] = useState(true)
  const [showAllowanceForm, setShowAllowanceForm] = useState(false)
  const [showAdjustForm, setShowAdjustForm] = useState(false)
  const [editingAllowance, setEditingAllowance] = useState<BroadcastAllowance | null>(null)
  const [deletingAllowance, setDeletingAllowance] = useState<BroadcastAllowance | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [e, a, adj] = await Promise.all([
        broadcastAdminService.getEntitlement(customerId),
        broadcastAdminService.listAllowances(customerId),
        broadcastAdminService.listAdjustments(customerId)
      ])
      setEntitlement(e.data)
      setAllowances(a.data || [])
      setAdjustments(adj.data || [])
    } catch (err) {
      showError('Failed to load entitlement', err instanceof Error ? err.message : 'Please try again')
    } finally {
      setLoading(false)
    }
  }, [customerId, showError])

  useEffect(() => { void load() }, [load])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading entitlement…
      </div>
    )
  }

  const b = entitlement?.balance
  const available = (b?.purchased ?? 0) + (b?.adjusted ?? 0)
  const pct = available > 0 ? Math.min(100, Math.round(((b?.consumed ?? 0) / available) * 100)) : 0

  return (
    <div className="space-y-6">
      {/* ── Balance ─────────────────────────────────────────────────────── */}
      {!entitlement?.hasAllowance ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">No credit allowance covers today</p>
            <p className="text-xs text-amber-800 mt-1">
              {organizationName ? `${organizationName} ` : 'This customer '}
              cannot submit broadcasts until a period is created. Enabling the feature without
              credits just produces a support ticket, so add the allowance here as well.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Ticket className="h-4 w-4 text-blue-600" />
                {entitlement.allowance?.periodLabel || 'Current period'}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {fmtDate(entitlement.allowance!.periodStart)} – {fmtDate(entitlement.allowance!.periodEnd)}
                {' · '}1 credit = up to {entitlement.allowance?.hoursPerCredit}h
              </p>
            </div>
            {entitlement.exhausted && (
              <span className="px-2 py-1 rounded-md text-xs font-semibold bg-red-100 text-red-700">
                Exhausted
              </span>
            )}
            {!entitlement.exhausted && entitlement.warning && (
              <span className="px-2 py-1 rounded-md text-xs font-semibold bg-amber-100 text-amber-700">
                80%+ used
              </span>
            )}
          </div>

          <div className="grid grid-cols-4 gap-4 mb-4">
            {[
              { label: 'Purchased', value: b?.purchased ?? 0, tone: 'text-gray-900' },
              { label: 'Granted', value: b?.adjusted ?? 0, tone: (b?.adjusted ?? 0) >= 0 ? 'text-blue-700' : 'text-red-700' },
              { label: 'Used', value: b?.consumed ?? 0, tone: 'text-gray-900' },
              { label: 'Remaining', value: b?.remaining ?? 0, tone: (b?.remaining ?? 0) > 0 ? 'text-emerald-700' : 'text-red-700' }
            ].map((k) => (
              <div key={k.label}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{k.label}</p>
                <p className={`text-2xl font-bold ${k.tone}`}>{k.value}</p>
              </div>
            ))}
          </div>

          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1.5">
            {b?.consumed ?? 0} of {available} credits used.
            {' '}Content mix never affects the charge — a rain-delayed game costs the same credit as a clean one.
          </p>
        </div>
      )}

      {/* ── Actions ─────────────────────────────────────────────────────── */}
      <div className="flex gap-2">
        <button
          onClick={() => { setShowAllowanceForm((v) => !v); setShowAdjustForm(false) }}
          className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add period
        </button>
        <button
          onClick={() => { setShowAdjustForm((v) => !v); setShowAllowanceForm(false) }}
          disabled={!entitlement?.hasAllowance}
          className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          title={entitlement?.hasAllowance ? 'Grant or correct credits' : 'Create a period first'}
        >
          <Ticket className="h-3.5 w-3.5 mr-1.5" /> Grant credits
        </button>
      </div>

      {showAllowanceForm && (
        <AllowanceForm
          customerId={customerId}
          saving={saving}
          setSaving={setSaving}
          onDone={async () => { setShowAllowanceForm(false); await load() }}
          onSuccess={showSuccess}
          onError={showError}
        />
      )}

      {editingAllowance && (
        <AllowanceForm
          key={editingAllowance.id}
          editing={editingAllowance}
          customerId={customerId}
          saving={saving}
          setSaving={setSaving}
          onDone={async () => { setEditingAllowance(null); await load() }}
          onSuccess={showSuccess}
          onError={showError}
        />
      )}

      {deletingAllowance && (
        <div className="rounded-lg border p-4 bg-red-50 border-red-200">
          <p className="text-sm font-semibold text-gray-900 mb-1">
            Delete the period “{deletingAllowance.period_label || `${fmtDate(deletingAllowance.period_start)} – ${fmtDate(deletingAllowance.period_end)}`}”?
          </p>
          <p className="text-xs text-red-900 mb-3">
            Only for a period entered by mistake. It is refused if any broadcast was charged or any
            credits were granted inside its dates — edit it instead.
          </p>
          <div className="flex gap-2">
            <button disabled={saving} onClick={async () => {
              setSaving(true)
              try {
                await broadcastAdminService.deleteAllowance(customerId, deletingAllowance.id)
                showSuccess('Period deleted', deletingAllowance.period_label || '')
                setDeletingAllowance(null)
                await load()
              } catch (err) {
                showError('Could not delete period', err instanceof Error ? err.message : 'Please try again')
              } finally { setSaving(false) }
            }}
              className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 disabled:opacity-50">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}Delete
            </button>
            <button onClick={() => setDeletingAllowance(null)}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {showAdjustForm && (
        <AdjustmentForm
          customerId={customerId}
          saving={saving}
          setSaving={setSaving}
          onDone={async () => { setShowAdjustForm(false); await load() }}
          onSuccess={showSuccess}
          onError={showError}
        />
      )}

      {/* ── Periods ─────────────────────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Periods</h3>
        {allowances.length === 0 ? (
          <p className="text-xs text-gray-500">None yet.</p>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left">
                  {['Period', 'Dates', 'Credits', 'Band', 'Notes'].map((h) => (
                    <th key={h} className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                  ))}
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {allowances.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-900">{a.period_label || '—'}</td>
                    <td className="px-4 py-2 text-gray-600">{fmtDate(a.period_start)} – {fmtDate(a.period_end)}</td>
                    <td className="px-4 py-2 text-gray-900">{a.credits_purchased}</td>
                    <td className="px-4 py-2 text-gray-600">{a.hours_per_credit}h</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{a.notes || '—'}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button title="Edit period" onClick={() => { setEditingAllowance(a); setDeletingAllowance(null); setShowAllowanceForm(false); setShowAdjustForm(false) }}
                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button title="Delete period" onClick={() => { setDeletingAllowance(a); setEditingAllowance(null) }}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Adjustment history ──────────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
          <History className="h-4 w-4 text-gray-400" /> Adjustment history
        </h3>
        {adjustments.length === 0 ? (
          <p className="text-xs text-gray-500">No credits granted or corrected.</p>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
            {adjustments.map((adj) => (
              <div key={adj.id} className="px-4 py-2.5 flex items-start gap-3">
                <span className={`font-bold text-sm w-10 flex-shrink-0 ${adj.credits > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {adj.credits > 0 ? '+' : ''}{adj.credits}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-800">
                    {REASONS.find((r) => r.value === adj.reason_code)?.label ?? adj.reason_code}
                    {adj.broadcast_title && <span className="font-normal text-gray-500"> · {adj.broadcast_title}</span>}
                  </p>
                  <p className="text-xs text-gray-600">{adj.notes}</p>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{fmtDate(adj.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

export default BroadcastEntitlement
